// « Administration fiscale » — what the screen says, pure (P3 F4c-2). Labels are French: the readers are the operator
// and an inspector of the DGFiP.

import type { FiscalAuditArchive, FiscalAuditClosure, FiscalAuditKey, OtsState } from '@lemontaigu/pms-core';

export const CLOSURE_KIND_FR: Record<FiscalAuditClosure['kind'], string> = {
  daily: 'Journalière',
  monthly: 'Mensuelle',
  fiscal_year: 'Exercice',
};

export const ARCHIVE_KIND_FR: Record<FiscalAuditArchive['kind'], string> = {
  monthly: 'Mensuelle',
  fiscal_year: 'Exercice',
  on_demand: 'À la demande',
};

export const TENDER_FR: Record<string, string> = {
  card: 'Carte bancaire',
  amex: 'American Express',
  vad: 'Vente à distance',
  cash: 'Espèces',
  cheque: 'Chèque',
  ancv: 'Chèques-vacances',
  bank_transfer: 'Virement',
};

export const MOVEMENT_FR: Record<string, string> = {
  receipt: 'encaissement',
  refund: 'remboursement',
  reversal: 'annulation',
  forfeit: 'arrhes conservées',
  transfer_in: 'transfert reçu',
  transfer_out: 'transfert sortant',
};

export function tenderFr(tender: string | null, movement?: string): string {
  const t = tender ? TENDER_FR[tender] ?? tender : 'Sans moyen';
  return movement ? `${t} — ${MOVEMENT_FR[movement] ?? movement}` : t;
}

export function otsFr(ots: OtsState): string {
  return ots === 'bitcoin' ? 'OpenTimestamps ancré (Bitcoin)' : ots === 'pending' ? 'OpenTimestamps en attente' : 'OpenTimestamps absent';
}

/** A clôture's proof in one line: signed by which key, and by which witnesses. */
export function closureSealFr(c: Pick<FiscalAuditClosure, 'keyId' | 'tsa' | 'ots'>): string {
  if (!c.keyId) return 'Non signée';
  return [`Signée (${c.keyId})`, c.tsa ? 'horodatée (RFC 3161)' : 'horodatage absent', otsFr(c.ots)].join(' · ');
}

/** Is the clôture's proof complete (signed and both witnesses)? A pending OTS is complete enough — it completes itself. */
export function closureSealed(c: Pick<FiscalAuditClosure, 'keyId' | 'tsa' | 'ots'>): boolean {
  return c.keyId !== null && c.tsa && c.ots !== null;
}

/** The period as a reader says it: one day, a month, an exercice, or a range of days. */
export function periodFr(kind: string, start: string, end: string): string {
  const d = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  const m = (iso: string) => `${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  if (kind === 'daily' || start === end) return d(start);
  if (kind === 'monthly') return m(start);
  if (kind === 'fiscal_year') return `exercice ${m(start)} → ${m(end)}`;
  return `${d(start)} → ${d(end)}`;
}

export function keyPurposeFr(k: Pick<FiscalAuditKey, 'purpose'>): string {
  return k.purpose === 'record_mac' ? 'Scellement des enregistrements (HMAC, clé secrète)'
    : k.purpose === 'head_signature' ? 'Signature des clôtures et archives (Ed25519)'
    : k.purpose;
}

export function keyStateFr(k: Pick<FiscalAuditKey, 'validTo' | 'revokedAt'>, nowIso: string): string {
  if (k.revokedAt) return `Révoquée le ${k.revokedAt.slice(0, 10)}`;
  if (k.validTo && k.validTo <= nowIso) return `Close le ${k.validTo.slice(0, 10)}`;
  return 'En service';
}

/** Short form of a 64-hex hash, the way it is compared by eye. */
export function shortHash(h: string | null | undefined): string {
  return h ? `${h.slice(0, 12)}…${h.slice(-6)}` : '—';
}
